<template>
  <div class="mt-4 mt-sm-5">
    <!-- Header -->
    <b-row>
      <b-col>
        <div class="page-subtitle mt-5 mb-4">Tradie Supervisors</div>
        <p class="text-muted mb-3">
          Supervisors can log in to the web app and view their assigned
          projects.
        </p>
      </b-col>
    </b-row>
    <div class="d-flex justify-content-end mb-3">
      <b-button
        v-if="canViewTradies && canManageUsers"
        variant="primary"
        class="add-supervisor-btn"
        @click="openAddModal"
      >
        + Add Supervisor
      </b-button>
    </div>

    <!-- Supervisor list -->
    <b-overlay :show="loadingSupervisors" rounded="sm">
      <b-alert v-if="supervisorLoadError" show variant="warning" class="mb-3">
        {{ supervisorLoadError }}
      </b-alert>
      <div class="table-wrapper">
        <b-table
          :fields="computedFields"
          :items="supervisors"
          :tbody-tr-class="getRowClass"
          class="mt-1 admins-table"
          :class="{
            'admins-table--clickable': canManageUsers || canAssignTokens,
          }"
          @row-clicked="onRowClicked($event)"
          :hover="canManageUsers || canAssignTokens"
          show-empty
        >
          <template #cell(full_name)="{ item }">
            <div class="cell-content-wrapper">
              <span>{{ item.full_name || item.email || '-' }}</span>
              <div
                v-if="!item.can_access"
                class="lock-overlay"
                :ref="`lock-overlay-${item.id}`"
              >
                <b-icon-lock-fill class="mr-2" style="font-size: 1.1rem" />
                <span>Locked - Assign token to view</span>
              </div>
            </div>
          </template>

          <template #cell(email)="{ item }">
            <span v-if="item.can_access">{{ item.email || '-' }}</span>
          </template>

          <template #cell(role)="{ item }">
            <b-badge v-if="item.can_access" variant="info">Supervisor</b-badge>
          </template>

          <template #cell(projects)="{ item }">
            <b-badge
              v-if="item.can_access"
              variant="light"
              class="border"
              style="font-weight: 500"
            >
              {{ item._projectCount != null ? item._projectCount : '…' }}
              {{ item._projectCount === 1 ? 'project' : 'projects' }}
            </b-badge>
          </template>

          <template #cell(phone_number)="{ item }">
            <span v-if="item.can_access">{{ item.phone_number || '-' }}</span>
          </template>

          <template #cell(token_expiry)="{ item }">
            <span v-if="item.can_access">
              <span v-if="tokenExpiryDates[item.id]">
                <span
                  :class="{
                    'text-danger font-weight-bold': isExpiringSoon(
                      tokenExpiryDates[item.id],
                      30,
                    ),
                    'text-warning':
                      isExpiringSoon(tokenExpiryDates[item.id], 60) &&
                      !isExpiringSoon(tokenExpiryDates[item.id], 30),
                  }"
                >
                  {{ formatExpiryDate(tokenExpiryDates[item.id]) }}
                </span>
              </span>
              <span
                v-else-if="tokenExpiryDates[item.id] === null"
                class="text-muted"
                >-</span
              >
              <span v-else class="text-muted">Loading...</span>
            </span>
            <span v-else>-</span>
          </template>

          <template #head(remove)>
            <span class="text-center d-block">Remove</span>
          </template>

          <template #cell(remove)="{ item }">
            <div class="text-center">
              <b-button
                variant="outline-light"
                style="height: 32px"
                class="px-2"
                title="Remove supervisor"
                :disabled="removingId === item.id"
                @click.stop="openRemoveWarning(item)"
              >
                <b-icon-x-circle-fill variant="danger" />
              </b-button>
            </div>
          </template>

          <template #empty>
            <div class="text-muted py-4 text-center" style="font-size: 13px">
              No supervisors yet. Click <strong>+ Add Supervisor</strong> to get
              started.
            </div>
          </template>
        </b-table>
      </div>

      <div
        v-if="supervisorMeta && supervisorMeta.last_page > 1"
        class="mt-3 d-flex justify-content-center"
      >
        <b-pagination
          v-model="supervisorPage"
          :total-rows="supervisorMeta.total"
          :per-page="supervisorPerPage"
          size="sm"
          @change="fetchSupervisors"
        />
      </div>
    </b-overlay>

    <!-- REMOVE WARNING modal -->
    <b-modal
      v-if="removeWarning.sup"
      v-model="removeWarning.open"
      title="Remove Supervisor"
      :hide-footer="false"
      @hidden="resetRemoveWarning"
    >
      <div>
        <template
          v-if="
            !removeWarning.sup.supervisor_project_ids ||
            removeWarning.sup.supervisor_project_ids.length <= 1
          "
        >
          <p>
            Are you sure you want to remove
            <strong>{{
              removeWarning.sup.full_name || removeWarning.sup.email
            }}</strong>
            as a supervisor?
          </p>
          <p class="text-muted small mb-0">
            This will remove their supervisor access entirely.
          </p>
        </template>
        <template v-else>
          <p>
            Select which project(s) to remove
            <strong>{{
              removeWarning.sup.full_name || removeWarning.sup.email
            }}</strong>
            from:
          </p>
          <b-overlay :show="removeWarning.loading" rounded="sm">
            <div
              v-if="
                !removeWarning.loading && removeWarning.projects.length === 0
              "
              class="text-muted small"
            >
              No projects found.
            </div>
            <template v-else>
              <b-form-checkbox
                v-model="allRemoveProjectsSelected"
                class="mb-2 pb-2 border-bottom"
                :indeterminate="someRemoveProjectsSelected"
              >
                Select all
              </b-form-checkbox>
              <b-form-checkbox-group
                v-model="removeWarning.selectedProjectIds"
                stacked
              >
                <b-form-checkbox
                  v-for="p in removeWarning.projects"
                  :key="p.id"
                  :value="p.id"
                  class="mb-1"
                >
                  {{ p.name }}
                </b-form-checkbox>
              </b-form-checkbox-group>
            </template>
          </b-overlay>
        </template>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="removeWarning.open = false"
        >
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="
            removeWarning.loading ||
            (removeWarning.sup.supervisor_project_ids &&
              removeWarning.sup.supervisor_project_ids.length > 1 &&
              removeWarning.selectedProjectIds.length === 0)
          "
          @click="confirmRemove"
        >
          Remove
        </b-button>
      </template>
    </b-modal>

    <!-- ADD modal (tradie picker + project picker) -->
    <b-modal
      v-model="addModalOpen"
      title="Add Supervisor"
      size="xl"
      hide-footer
      @hidden="resetAddModal"
    >
      <div class="row no-gutters" style="min-height: 420px">
        <!-- Left: Tradie picker -->
        <div class="col-md-5 pr-md-3">
          <p class="text-muted mb-2" style="font-size: 12px">
            Select one tradie
          </p>
          <b-form-input
            v-model="tradieSearch"
            placeholder="Search tradies…"
            size="sm"
            debounce="300"
            class="mb-2"
          />

          <b-overlay :show="loadingTradies" rounded="sm">
            <div class="tradie-scroll">
              <div
                v-for="t in modalTradies"
                :key="t.id"
                class="tradie-pick-row"
                :class="{
                  'tradie-pick-row--selected': selectedTradieId === t.id,
                  'tradie-pick-row--disabled': !hasName(t),
                }"
                @click="hasName(t) && (selectedTradieId = t.id)"
              >
                <div class="tradie-pick-radio">
                  <span
                    class="radio-dot"
                    :class="{ active: selectedTradieId === t.id }"
                  />
                </div>
                <b-avatar
                  :src="avatarUrl(t.avatar)"
                  :text="initials(t.full_name || t.email)"
                  size="28px"
                  variant="secondary"
                  class="flex-shrink-0"
                />
                <div class="tradie-pick-label">
                  <div class="tradie-pick-name">
                    {{ t.full_name || t.email || '-' }}
                  </div>
                  <div v-if="t.full_name" class="tradie-pick-email">
                    {{ t.email }}
                  </div>
                  <div v-else class="tradie-pick-warning">
                    Needs a name before they can be a supervisor
                  </div>
                </div>
              </div>

              <div
                v-if="modalTradies.length === 0 && !loadingTradies"
                class="text-muted text-center py-4 small"
              >
                No tradies found.
              </div>
            </div>
          </b-overlay>

          <div
            v-if="tradieMeta && tradieMeta.last_page > 1"
            class="mt-2 d-flex justify-content-center"
          >
            <b-pagination
              v-model="tradiePage"
              :total-rows="tradieMeta.total"
              :per-page="tradiePerPage"
              size="sm"
              @change="onTradiePage"
            />
          </div>
        </div>

        <!-- Divider -->
        <div
          class="d-none d-md-block"
          style="width: 1px; background: #dee2e6; margin: 0 16px"
        />

        <!-- Right: Project picker -->
        <div class="col-md-6">
          <p class="text-muted mb-2" style="font-size: 12px">
            Select at least one project
          </p>
          <template v-if="selectedTradieId">
            <project-picker
              v-model="selectedProjectIds"
              :per-page="projectPerPage"
            />
          </template>
          <div v-else class="text-muted text-center py-5 small border rounded">
            Select a tradie first
          </div>
        </div>
      </div>

      <hr class="mt-3 mb-2" />
      <div class="d-flex justify-content-end" style="gap: 8px">
        <b-button variant="outline-secondary" @click="addModalOpen = false"
          >Cancel</b-button
        >
        <b-button
          variant="primary"
          :disabled="!selectedTradieId || !selectedProjectIds.length || saving"
          @click="saveSupervisor"
        >
          {{ saving ? 'Saving…' : 'Save Supervisor' }}
        </b-button>
      </div>
    </b-modal>

    <!-- ASSIGN TOKEN modal -->
    <assign-token-modal
      v-if="isAssigningToken && canAssignTokens"
      :showModal="isAssigningToken"
      :tradieToAssign="tradieToAssign"
      @closeModal="onAssignModalClose"
      @assign="onAssignToken"
      @purchaseTokens="onAssignModalPurchaseTokens"
    />

    <!-- PURCHASE TOKEN modal -->
    <purchase-token-modal
      v-if="canPurchaseTokens"
      :showModal="isPurchasingToken"
      @closeModal="purchasingToken(false)"
      @purchase="onPurchaseTokens"
    />

    <!-- EDIT modal (projects only, tradie fixed) -->
    <b-modal
      v-model="editModalOpen"
      title="Edit Supervisor"
      size="lg"
      hide-footer
      @hidden="resetEditModal"
    >
      <!-- Tradie header (always visible) -->
      <div
        v-if="editingSupervisor"
        class="d-flex align-items-center mb-3 p-3 bg-light rounded"
      >
        <b-avatar
          :src="avatarUrl(editingSupervisor.avatar)"
          :text="
            initials(editingSupervisor.full_name || editingSupervisor.email)
          "
          size="40px"
          variant="secondary"
          class="mr-3"
        />
        <div>
          <div class="font-weight-bold">
            {{ editingSupervisor.full_name || editingSupervisor.email || '-' }}
          </div>
          <div v-if="editingSupervisor.full_name" class="text-muted small">
            {{ editingSupervisor.email }}
          </div>
        </div>
      </div>

      <!-- Single spinner while fetching tradie data -->
      <div v-if="loadingEditData" class="text-center py-5">
        <b-spinner variant="primary" />
        <div class="mt-2 text-muted small">Loading project assignments…</div>
      </div>

      <!-- Picker only mounts after selectedProjectIds is populated -->
      <template v-else>
        <p class="font-weight-bold mb-1" style="font-size: 13px">
          Assigned Projects
        </p>
        <project-picker
          v-model="selectedProjectIds"
          :per-page="projectPerPage"
        />
      </template>

      <hr class="mt-3 mb-2" />
      <div class="d-flex justify-content-end" style="gap: 8px">
        <b-button variant="outline-secondary" @click="editModalOpen = false"
          >Cancel</b-button
        >
        <b-button
          variant="primary"
          :disabled="saving || loadingEditData || !selectedProjectIds.length"
          @click="saveSupervisor"
        >
          {{ saving ? 'Saving…' : 'Save' }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { canBuyTokens } from '@/utils/tokenAccess'
import { mapGetters, mapActions } from 'vuex'
import ProjectPicker from './SupervisorProjectPicker.vue'
import AssignTokenModal from '@/components/modals/AssignTokenModal'
import PurchaseTokenModal from '@/components/modals/PurchaseTokenModal'
import { applyTokenExpiryFromListTradies } from '@/utils/tradieListToken'
import { avatarUrl } from '@/utils/avatar'

export default {
  name: 'CompanyTradieAccessList',
  components: { ProjectPicker, AssignTokenModal, PurchaseTokenModal },

  data() {
    return {
      // Supervisor list
      supervisors: [],
      supervisorMeta: null,
      supervisorPage: 1,
      supervisorPerPage: 20,
      loadingSupervisors: false,
      supervisorLoadError: null,
      removingId: null,
      tokenExpiryDates: {},

      // Add modal
      addModalOpen: false,
      modalTradies: [],
      tradieMeta: null,
      tradiePage: 1,
      tradiePerPage: 10,
      tradieSearch: '',
      loadingTradies: false,

      // Assign token modal
      isAssigningToken: false,
      tradieToAssign: null,

      // Purchase token modal
      isPurchasingToken: false,

      // Edit modal
      editModalOpen: false,
      editingSupervisor: null,
      loadingEditData: false,

      // Remove warning modal
      removeWarning: {
        open: false,
        sup: null,
        projects: [],
        selectedProjectIds: [],
        loading: false,
      },

      // Shared between add/edit
      selectedTradieId: null,
      selectedProjectIds: [],
      projectPerPage: 15,
      saving: false,
    }
  },

  computed: {
    ...mapGetters('auth', ['hasPermission', 'user']),

    canViewTradies() {
      return this.hasPermission('tradies.view')
    },

    /**
     * Free tier / still inside the 30-day trial — `is_free` on /auth/me, the
     * same flag the router and every token modal gate on. These companies
     * skip the token economy, so token-only UI like the Token Expiry column
     * is hidden for them.
     */
    isFree() {
      return !!(this.user && this.user.is_free)
    },

    canManageUsers() {
      return this.hasPermission('settings.users.manage')
    },

    canAssignTokens() {
      return (
        (this.user && this.user.is_primary) ||
        this.hasPermission('settings.billing.assign')
      )
    },

    canPurchaseTokens() {
      return canBuyTokens(this.user, this.hasPermission)
    },

    computedFields() {
      // Responsive priority columns: essentials show on phones; lower-priority
      // columns appear progressively as the screen widens (no horizontal scroll).
      const atSm = 'd-none d-sm-table-cell'
      const atMd = 'd-none d-md-table-cell'
      const atLg = 'd-none d-lg-table-cell'
      const atXl = 'd-none d-xl-table-cell'

      const fields = [
        { key: 'full_name', label: 'Name' },
        {
          key: 'projects',
          label: 'Projects',
          thClass: atSm,
          tdClass: `cell-hide-when-locked ${atSm}`,
        },
        {
          key: 'role',
          label: 'Role',
          thClass: atMd,
          tdClass: `cell-hide-when-locked ${atMd}`,
        },
        {
          key: 'email',
          label: 'Email',
          thClass: atMd,
          tdClass: `cell-hide-when-locked ${atMd}`,
        },
        {
          key: 'phone_number',
          label: 'Phone Number',
          thClass: atLg,
          tdClass: `cell-hide-when-locked ${atLg}`,
        },
        // Free tier / trial companies skip the token economy entirely, so
        // there's no expiry to show — omit the column for them.
        ...(this.isFree
          ? []
          : [
              {
                key: 'token_expiry',
                label: 'Token Expiry',
                thClass: atXl,
                tdClass: `cell-hide-when-locked ${atXl}`,
              },
            ]),
      ]
      if (this.canManageUsers) fields.push({ key: 'remove', label: '' })
      return fields
    },

    someRemoveProjectsSelected() {
      const { selectedProjectIds, projects } = this.removeWarning
      return (
        selectedProjectIds.length > 0 &&
        selectedProjectIds.length < projects.length
      )
    },

    allRemoveProjectsSelected: {
      get() {
        const { selectedProjectIds, projects } = this.removeWarning
        return (
          projects.length > 0 && selectedProjectIds.length === projects.length
        )
      },
      set(checked) {
        this.removeWarning.selectedProjectIds = checked
          ? this.removeWarning.projects.map((p) => p.id)
          : []
      },
    },
  },

  watch: {
    tradieSearch() {
      this.tradiePage = 1
      this.fetchModalTradies()
    },
  },

  mounted() {
    this.fetchSupervisors()
  },

  methods: {
    avatarUrl,

    getRowClass(item) {
      if (!item) return ''
      return item.can_access ? '' : 'row-locked'
    },

    onRowClicked(item) {
      if (!item.can_access) {
        if (this.canAssignTokens) this.assigningToken(item)
      } else if (this.canManageUsers) {
        this.openEditModal(item)
      }
    },

    // ── Supervisor list ─────────────────────────────────────────────────────

    async fetchSupervisors() {
      this.loadingSupervisors = true
      this.supervisorLoadError = null
      this.tokenExpiryDates = {}
      try {
        const { data } = await this.$api.get(
          `companies/tradies?limit=${this.supervisorPerPage}&page=${this.supervisorPage}` +
            `&search=&archived_only=false&supervisor_only=true`,
        )
        const list = Array.isArray(data.data) ? data.data : []
        this.supervisors = list.map((t) => ({
          ...t,
          _projectCount: Array.isArray(t.supervisor_project_ids)
            ? t.supervisor_project_ids.length
            : null,
        }))
        this.supervisorMeta = data.meta || null
        applyTokenExpiryFromListTradies(this, this.supervisors)
      } catch (e) {
        this.supervisors = []
        this.supervisorMeta = null
        const status = e && e.response && e.response.status
        if (status === 403) {
          this.supervisorLoadError =
            'Unable to load supervisors. Your role also needs the View tradies permission to list tradie accounts.'
        }
      } finally {
        this.loadingSupervisors = false
      }
    },

    async openRemoveWarning(sup) {
      this.removeWarning = {
        open: true,
        sup,
        projects: [],
        selectedProjectIds: [],
        loading: false,
      }

      const projectIds = Array.isArray(sup.supervisor_project_ids)
        ? sup.supervisor_project_ids
        : []

      if (projectIds.length > 1) {
        this.removeWarning.loading = true
        try {
          const { data } = await this.$api.get('projects', {
            params: { limit: 200, 'filter[is_archived]': 0 },
          })
          const all = Array.isArray(data.data) ? data.data : []
          this.removeWarning.projects = all
            .filter((p) => projectIds.includes(p.id))
            .map((p) => ({ id: p.id, name: p.name }))
        } catch (_e) {
          // no-op — modal will show fallback
        } finally {
          this.removeWarning.loading = false
        }
      }
    },

    resetRemoveWarning() {
      this.removeWarning = {
        open: false,
        sup: null,
        projects: [],
        selectedProjectIds: [],
        loading: false,
      }
    },

    async confirmRemove() {
      const { sup, selectedProjectIds } = this.removeWarning
      const projectIds = Array.isArray(sup.supervisor_project_ids)
        ? sup.supervisor_project_ids
        : []
      const isSingleProject = projectIds.length <= 1

      this.removingId = sup.id
      this.removeWarning.open = false

      try {
        let isSupervisor, newProjectIds, toastMessage

        if (isSingleProject) {
          isSupervisor = false
          newProjectIds = []
          toastMessage = `${
            sup.full_name || sup.email || 'Tradie'
          } removed as supervisor.`
        } else {
          newProjectIds = projectIds.filter(
            (id) => !selectedProjectIds.includes(id),
          )
          isSupervisor = newProjectIds.length > 0

          if (newProjectIds.length === 0) {
            toastMessage = `${
              sup.full_name || sup.email || 'Tradie'
            } removed as supervisor.`
          } else if (selectedProjectIds.length === 1) {
            const foundProject = this.removeWarning.projects.find(
              (p) => p.id === selectedProjectIds[0],
            )
            const projectName = (foundProject && foundProject.name) || 'project'
            toastMessage = `${
              sup.full_name || sup.email || 'Tradie'
            } removed from ${projectName}.`
          } else {
            toastMessage = `${
              sup.full_name || sup.email || 'Tradie'
            } removed from ${selectedProjectIds.length} projects.`
          }
        }

        await this.$store.dispatch('company/updateTradieSupervisor', {
          tradieId: sup.id,
          isSupervisor,
          projectIds: newProjectIds,
        })

        this.$bvToast.toast(toastMessage, {
          title: 'Removed',
          variant: 'success',
          solid: true,
        })

        await this.fetchSupervisors()
      } catch (_e) {
        this.$bvToast.toast('Failed to remove supervisor.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.removingId = null
        this.resetRemoveWarning()
      }
    },

    // ── Add modal ───────────────────────────────────────────────────────────

    openAddModal() {
      if (!this.canViewTradies) return

      this.selectedTradieId = null
      this.selectedProjectIds = []
      this.tradiePage = 1
      this.tradieSearch = ''
      this.addModalOpen = true
      this.fetchModalTradies()
    },

    resetAddModal() {
      this.selectedTradieId = null
      this.selectedProjectIds = []
      this.modalTradies = []
      this.tradieSearch = ''
      this.tradiePage = 1
    },

    async fetchModalTradies() {
      this.loadingTradies = true
      try {
        const { data } = await this.$api.get(
          `companies/tradies?limit=${this.tradiePerPage}&page=${this.tradiePage}` +
            `&search=${encodeURIComponent(
              this.tradieSearch,
            )}&archived_only=false`,
        )
        this.modalTradies = Array.isArray(data.data) ? data.data : []
        this.tradieMeta = data.meta || null
      } catch (e) {
        this.modalTradies = []
      } finally {
        this.loadingTradies = false
      }
    },

    onTradiePage(page) {
      this.tradiePage = page
      this.fetchModalTradies()
    },

    // ── Edit modal ──────────────────────────────────────────────────────────

    async openEditModal(sup) {
      this.editingSupervisor = sup
      this.selectedTradieId = sup.id
      this.selectedProjectIds = []
      this.editModalOpen = true
      this.loadingEditData = true
      try {
        // Fetch full tradie data to get supervisor_project_ids (list response may be slim)
        const { data } = await this.$api.get(`companies/tradie/${sup.id}`)
        const tradie = data.data
        this.selectedProjectIds = Array.isArray(tradie.supervisor_project_ids)
          ? [...tradie.supervisor_project_ids]
          : []
      } catch (e) {
        // Fallback to whatever the list had
        this.selectedProjectIds = Array.isArray(sup.supervisor_project_ids)
          ? [...sup.supervisor_project_ids]
          : []
      } finally {
        this.loadingEditData = false
      }
    },

    resetEditModal() {
      this.editingSupervisor = null
      this.selectedTradieId = null
      this.selectedProjectIds = []
    },

    // ── Save ────────────────────────────────────────────────────────────────

    hasName(tradie) {
      return !!(tradie && tradie.full_name && String(tradie.full_name).trim())
    },

    async saveSupervisor() {
      if (!this.selectedTradieId) return
      // A tradie with no name yet can't be made a supervisor (they'd show as a
      // blank row everywhere they're picked). The picker row is already
      // unclickable for these; this guards the Add flow against any slip.
      if (this.addModalOpen && !this.editModalOpen) {
        const picked = this.modalTradies.find(
          (t) => t.id === this.selectedTradieId,
        )
        if (picked && !this.hasName(picked)) {
          this.$bvToast.toast(
            'This tradie needs a name before they can be a supervisor.',
            { title: 'Name required', variant: 'warning', solid: true },
          )
          return
        }
      }
      this.saving = true
      try {
        await this.$store.dispatch('company/updateTradieSupervisor', {
          tradieId: this.selectedTradieId,
          isSupervisor: true,
          projectIds: this.selectedProjectIds,
        })
        this.$bvToast.toast('Supervisor saved.', {
          title: 'Saved',
          variant: 'success',
          solid: true,
        })
        this.addModalOpen = false
        this.editModalOpen = false
        await this.fetchSupervisors()
      } catch (e) {
        this.$bvToast.toast('Failed to save supervisor.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.saving = false
      }
    },

    // ── Token assignment ────────────────────────────────────────────────────

    assigningToken(item) {
      this.tradieToAssign = item
      this.isAssigningToken = true
    },

    async onAssignToken() {
      if (!this.tradieToAssign) return
      const companyId = this.user && this.user.company_id
      if (!companyId) {
        this.$bvToast.toast('Company ID not found.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        return
      }
      try {
        await this.assign(this.tradieToAssign.id, companyId)
        this.$bvToast.toast('Token assigned to supervisor.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
        this.isAssigningToken = false
        this.tradieToAssign = null
        await this.fetchSupervisors()
        await this.$store.dispatch('token/getBalance')
      } catch (err) {
        const message =
          (err && err.data && err.data.message) || 'Failed to assign token.'
        this.$bvToast.toast(message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    onAssignModalClose() {
      this.isAssigningToken = false
      this.tradieToAssign = null
    },

    onAssignModalPurchaseTokens() {
      this.isAssigningToken = false
      this.tradieToAssign = null
      if (this.canPurchaseTokens) {
        this.purchasingToken(true)
      }
    },

    purchasingToken(status) {
      this.isPurchasingToken = status
    },

    async onPurchaseTokens(quantity) {
      if (!quantity || quantity < 1) {
        return
      }
      try {
        await this.$store.dispatch('token/purchase', quantity)
        await this.$store.dispatch('token/getBalance')
        this.purchasingToken(false)
      } catch (err) {
        const message =
          (err && err.data && err.data.message) || 'Failed to purchase tokens.'
        this.$bvToast.toast(message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    // ── Helpers ─────────────────────────────────────────────────────────────

    formatExpiryDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },

    isExpiringSoon(expiryDate, days) {
      if (!expiryDate) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const expiry = new Date(expiryDate)
      expiry.setHours(0, 0, 0, 0)
      const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24))
      return diffDays >= 0 && diffDays <= days
    },

    initials(str) {
      if (!str) return '?'
      return str
        .match(/(^\S\S?|\b\S)?/g)
        .join('')
        .match(/(^\S|\S$)?/g)
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },

    ...mapActions('token', {
      assign: 'assign',
    }),
  },
}
</script>

<style>
.row-locked > td {
  background-color: #f5f5f5 !important;
}

.row-locked > td.cell-hide-when-locked * {
  visibility: hidden;
}

.lock-overlay {
  color: #000000 !important;
}

.admins-table tbody tr {
  cursor: pointer;
}

/* Keep the "Locked" overlay confined to the row width so it wraps inside the
   row instead of spilling past the card. Spanning left:0 → right:0 makes it
   exactly the row's width at every screen size, so it scales with the row. */
.admins-table tr.row-locked .lock-overlay {
  left: 0;
  right: 0;
  width: auto;
  max-width: 100%;
  transform: translateY(-50%);
  white-space: normal;
  text-align: center;
  padding: 0 6px;
}

/* Phones: pull the columns together so the table fits inside the card without
   scrolling. Tighter cell padding + slightly smaller text; action buttons are
   compacted too. Desktop spacing is untouched (only applies < 576px). */
@media (max-width: 575.98px) {
  .admins-table th,
  .admins-table td {
    padding-left: 0.3rem;
    padding-right: 0.3rem;
    font-size: 0.85rem;
    vertical-align: middle;
    /* Long emails/names are unbreakable strings; allow them to wrap so a single
       cell can't push the table past the edge of the card. */
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  .admins-table td .btn {
    padding-left: 0.3rem !important;
    padding-right: 0.3rem !important;
  }
  /* Phones can't fit "Locked - Assign token to view", so show just the lock
     icon (the muted row content still signals it's locked). */
  .admins-table tr.row-locked .lock-overlay span {
    display: none;
  }
  .admins-table tr.row-locked .lock-overlay .b-icon {
    margin-right: 0 !important;
    font-size: 1.35rem !important;
  }
}
</style>

<style scoped>
/* Phones: scale the section heading spacing down. Column visibility is handled
   per-field (responsive priority columns), so no horizontal scroll is needed. */
@media (max-width: 575.98px) {
  .page-subtitle.mt-5 {
    margin-top: 1.25rem !important;
  }
  /* Full-width action button to match the search/add controls above. */
  .add-supervisor-btn {
    width: 100%;
    padding: 0.6rem 0.5rem;
    font-size: 0.9rem;
  }
}
@media (min-width: 576px) and (max-width: 991.98px) {
  .page-subtitle.mt-5 {
    margin-top: 2rem !important;
  }
}

/* Tradie picker in add modal */
.tradie-scroll {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.tradie-pick-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  cursor: pointer;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.1s;
}

.tradie-pick-row:last-child {
  border-bottom: none;
}

.tradie-pick-row:hover {
  background: #f5f7fa;
}

.tradie-pick-row--selected {
  background: #eef3ff !important;
}

.tradie-pick-row--disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.tradie-pick-row--disabled:hover {
  background: transparent;
}

.tradie-pick-radio {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.radio-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #ced4da;
  display: inline-block;
  transition: border-color 0.1s, background 0.1s;
}

.radio-dot.active {
  border-color: #007bff;
  background: #007bff;
  box-shadow: inset 0 0 0 3px #fff;
}

.tradie-pick-label {
  min-width: 0;
  flex: 1;
}

.tradie-pick-name {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tradie-pick-email {
  font-size: 11px;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tradie-pick-warning {
  font-size: 11px;
  color: #b45309;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
